import React from "react";
import { language } from "../../../languageProvider/language/language";
import {
  defaultLanguage,
  INTERACTIVITY_TYPE_TO_LABEL_KEY,
} from "../../../helper/utility";

/**
 * Horizontal row of removable chips showing currently active filters
 * (category, tags, promos, brands, name search). Mobile-only.
 *
 * Returns null when no filter is active.
 *
 * Props:
 *  - categoryFilter: string ("" or "Tutti" → not rendered)
 *  - tags: Array<{id, nome, immagine}> (lookup source for tag labels)
 *  - promos: Array<{id, nome, img}> (lookup source for promo labels)
 *  - brands: Array<{name, logo}> (not used for labels; brand label = name)
 *  - nameFilter: string
 *  - selectedTags: Set<number|string>
 *  - selectedPromos: Set<number|string>
 *  - selectedBrands: Set<string>
 *  - onRemoveCategory(): cb
 *  - onRemoveTag(id): cb
 *  - onRemovePromo(id): cb
 *  - onRemoveBrand(name): cb
 *  - onRemoveName(): cb
 *  - onSaleActive: boolean
 *  - onRemoveOnSale(): cb
 *  - selectedInteractivities: Set<string>
 *  - onRemoveInteractivity(type): cb
 */
const ActiveFiltersRow = ({
  categoryFilter = "",
  tags = [],
  promos = [],
  // brands list (objects {name, logo}) currently unused — label = brand name
  // brands = [],
  nameFilter = "",
  selectedTags,
  selectedPromos,
  selectedBrands,
  onRemoveCategory,
  onRemoveTag,
  onRemovePromo,
  onRemoveBrand,
  onRemoveName,
  onSaleActive = false,
  onRemoveOnSale,
  selectedInteractivities,
  onRemoveInteractivity,
}) => {
  const tagSet = selectedTags || new Set();
  const promoSet = selectedPromos || new Set();
  const brandSet = selectedBrands || new Set();
  const interSet = selectedInteractivities || new Set();

  const hasCategory =
    categoryFilter && categoryFilter !== "" && categoryFilter !== "Tutti";
  const hasName = !!(nameFilter && String(nameFilter).trim() !== "");

  if (
    !hasCategory &&
    tagSet.size === 0 &&
    promoSet.size === 0 &&
    brandSet.size === 0 &&
    !hasName &&
    !onSaleActive &&
    interSet.size === 0
  ) {
    return null;
  }

  const removeLabelTpl =
    (language[defaultLanguage] && language[defaultLanguage].rimuovi_filtro) ||
    "Rimuovi filtro {label}";
  const buildAria = (label) =>
    removeLabelTpl.replace("{label}", String(label || ""));

  const tagById = new Map();
  (tags || []).forEach((t) => {
    if (t && t.id != null) tagById.set(t.id, t.nome || "");
  });
  const promoById = new Map();
  (promos || []).forEach((p) => {
    if (p && p.id != null) promoById.set(p.id, p.nome || "");
  });

  const chips = [];

  if (hasCategory) {
    chips.push({
      key: "cat",
      label: categoryFilter,
      onRemove: () => onRemoveCategory && onRemoveCategory(),
    });
  }

  if (onSaleActive) {
    const offerteLabel =
      (language[defaultLanguage] && language[defaultLanguage].offerte) ||
      "Offerte";
    chips.push({
      key: "onsale",
      label: offerteLabel,
      onRemove: () => onRemoveOnSale && onRemoveOnSale(),
    });
  }

  Array.from(interSet).forEach((type) => {
    const labelKey = INTERACTIVITY_TYPE_TO_LABEL_KEY[type] || type;
    const label =
      (language[defaultLanguage] && language[defaultLanguage][labelKey]) ||
      type;
    chips.push({
      key: `inter-${type}`,
      label,
      onRemove: () => onRemoveInteractivity && onRemoveInteractivity(type),
    });
  });

  Array.from(tagSet).forEach((id) => {
    const label = tagById.get(id) || String(id);
    chips.push({
      key: `tag-${id}`,
      label,
      onRemove: () => onRemoveTag && onRemoveTag(id),
    });
  });

  Array.from(promoSet).forEach((id) => {
    const label = promoById.get(id) || String(id);
    chips.push({
      key: `promo-${id}`,
      label,
      onRemove: () => onRemovePromo && onRemovePromo(id),
    });
  });

  Array.from(brandSet)
    .slice()
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((name) => {
      chips.push({
        key: `brand-${name}`,
        label: name,
        onRemove: () => onRemoveBrand && onRemoveBrand(name),
      });
    });

  if (hasName) {
    const label = `«${String(nameFilter).trim()}»`;
    chips.push({
      key: "name",
      label,
      onRemove: () => onRemoveName && onRemoveName(),
    });
  }

  return (
    <div className="pf-active-row" role="region" aria-label="Filtri attivi">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          className="pf-active-chip"
          aria-label={buildAria(c.label)}
          onClick={c.onRemove}
        >
          <span className="pf-active-chip__close" aria-hidden="true">
            ×
          </span>
          <span className="pf-active-chip__label">{c.label}</span>
        </button>
      ))}
    </div>
  );
};

export default ActiveFiltersRow;
